import type Competence from "constants/Competence";
import { metiersConditions } from "constants/ContactsMetiers/metiersConditions";
import type { Transfert } from "models";
import type {
  ContactMetier,
  ContactMetierPerimetre,
  Metier,
  MetierWithConditions,
  MetierWithIsSdeaCompetent,
  Poste,
  PosteSansAgent,
  TypeContactMetier,
} from "models/ContactsMetiers";
import { MetierNiveau } from "models/ContactsMetiers";

const posteSansAgent: PosteSansAgent = { codeAstre: null, libelle: "Hors compétence du SDEA" };

function isPosteAgent(poste: Poste | PosteSansAgent): poste is Poste {
  return Object.hasOwn(poste, "agent");
}

function isContactMetierPerimetre(
  contactMetier: ContactMetier
): contactMetier is ContactMetierPerimetre {
  return contactMetier.$type === "perimetre";
}

/**
 * Retourne une fonction de filtre par MetierNiveau selon un MetierNiveau requis.
 * @param niveauRequis Niveau _seuil_ déterminant si le niveau évalué est inclut ou non.
 *
 * @returns Un prédicat (fonction) évaluant un MetierNiveau selon les critères de `niveauRequis`.
 *
 * @example
 * ```typescript
 * const predicate = isNiveauToReturn(MetierNiveau.perimetre);
 *
 * predicate(MetierNiveau.territoire); // true
 * predicate(MetierNiveau.perimetre);  // true
 * predicate(MetierNiveau.commune);    // false
 * predicate(null);                    // true
 * ```
 */
function isNiveauToReturn(niveauRequis: MetierNiveau): (niveauEvalue: MetierNiveau) => boolean {
  switch (niveauRequis) {
    case MetierNiveau.commune:
    case MetierNiveau.perimetre:
      return () => true;
    case MetierNiveau.territoire:
      return (niveauCourant: MetierNiveau) => niveauCourant !== MetierNiveau.perimetre;
    default:
      return () => false;
  }
}

/**
 * Construit une liste de métiers avec le prédicat `isSdeaCompetentCondition` selon le niveau requis et les compétences.
 * @param metiers Liste des métiers à enrichir et filtrer
 * @param competences Liste des compétences utilisées pour filtrer les métiers
 * @param niveau Niveau _seuil_ utilisé pour filtre les métiers
 * @returns Une liste de métier avec `isSdeaCompetentCondition` afin d'évaluer par exemple leur affichage.
 */
function getMetierWithConditionsFilteredbyNiveau(
  metiers: Array<Metier>,
  competences: Array<Competence>,
  niveau: MetierNiveau
): Array<MetierWithConditions> {
  const niveauFilter = isNiveauToReturn(niveau);
  return metiers
    .filter(
      (m) => niveauFilter(m.niveau) && (m.competence == null || competences.includes(m.competence))
    )
    .reduce<Array<MetierWithConditions>>((acc, metier) => {
      const metierMetadata = metiersConditions[metier.code];
      if (metierMetadata != null) {
        acc.push({ ...metier, ...metierMetadata });
      } else {
        // En l'absence de `isSdeaCompetentCondition` trouvée, la règle est que le SDEA est compétent sur ce métier
        acc.push({ ...metier, isSdeaCompetentCondition: () => true });
      }
      return acc;
    }, [])
    .toSorted((m1, m2) => m1.libelle.localeCompare(m2.libelle));
}

/**
 * Retourne le `MetierNiveau` correspondant au `$type` d'un contact métier
 * @param type Type discriminant du contact métier
 * @returns La valeur de l'énum `MetierNiveau` si correspondance
 */
function getMetierNiveauFromTypeContactMetier(type: TypeContactMetier): MetierNiveau {
  switch (type) {
    // Commune & Commune Associée Déléguée partagent le même niveau
    case "commune":
    case "commune-associee-deleguee":
      return MetierNiveau.commune;
    case "perimetre":
      return MetierNiveau.perimetre;
    case "territoire":
      return MetierNiveau.territoire;
  }
}

/**
 * Retourne la liste de contacts métiers enrichis et filtrés selon le type demandé
 * et les conditions d'affichages des métiers non renseignés.
 */
function getContactMetiersToDisplay(
  $type: TypeContactMetier,
  donneesMetiers: {
    referentielMetiers: Array<Metier>;
    contactsMetiers: Array<ContactMetier>;
  },
  donneesFiltrage: {
    competences: Array<Competence>;
    transferts: Array<Transfert>;
  }
): Array<ContactMetier> {
  const { contactsMetiers, referentielMetiers } = donneesMetiers;
  const { competences, transferts } = donneesFiltrage;

  const metiersFiltres: Array<MetierWithConditions> = getMetierWithConditionsFilteredbyNiveau(
    referentielMetiers,
    competences,
    getMetierNiveauFromTypeContactMetier($type)
  );

  const { importants, autres } = metiersFiltres
    .map<MetierWithIsSdeaCompetent>(({ isSdeaCompetentCondition, ...metier }) => {
      // A partir de la liste de référence, évalue pour chaque métier la condition `isSdeaCompetentCondition`
      const isSdeaCompetent = isSdeaCompetentCondition(transferts);
      return { ...metier, isSdeaCompetent };
    })
    .reduce<Array<ContactMetier>>((acc, metier) => {
      const cms = contactsMetiers.filter((cm) => cm.metier.code === metier.code);
      if (cms != null && cms.length > 0) {
        // Cas nominal: ayant un contact métier, on l'affiche
        acc.push(
          ...cms.map((contactMetier) => {
            if (contactMetier.metier.ordreAffichage == null && metier.ordreAffichage != null) {
              contactMetier.metier.ordreAffichage = metier.ordreAffichage;
            }
            return contactMetier;
          })
        );
      } else if (!metier.isSdeaCompetent) {
        // Cas où le contact métier n'existe pas, si le métier n'est pas dans une compétence du SDEA
        // alors on affiche explicitement le libellé "Hors compétence du SDEA".
        //
        // TODO: actuellement si le contact métier n'est pas défini mais que le SDEA est compétent
        // on n'affiche pas ligne contrairement à ce qui était défini lors du refinement, mais dont
        // la règle a été revue en cours de développement de l'US #15920. Pour afficher un message
        // du type "SDEA, contact métier non défini" il _suffit_ de déplacer ce précédent `if` dans
        // l'assignation du poste.libellé ci-dessous.

        //@ts-expect-error la propriété manquante est "optionnelle" ici
        const fakeMetier: ContactMetier = {
          $type,
          id: `${metier.code}-contact-metier-fallback`,
          metier,
          postes: [posteSansAgent],
        };

        acc.push(fakeMetier);
      }
      return acc;
    }, [])
    // Séparation des contacts métiers importants des non importants
    .reduce<{
      /** Contacts métiers importants (avec un ordre d'affichage) */
      importants: Array<ContactMetier>;
      /** Contacts métiers sans ordre d'affichage */
      autres: Array<ContactMetier>;
    }>(
      (acc, contactMetier) => {
        const isMetierImportant = contactMetier.metier.ordreAffichage != null;
        acc[isMetierImportant ? "importants" : "autres"].push(contactMetier);
        return acc;
      },
      { importants: [], autres: [] }
    );

  return (
    importants
      // Tri des contacts métiers: selon l'ordre d'affichage pour les importants
      .toSorted((a, b) => (a.metier.ordreAffichage ?? 0) - (b.metier.ordreAffichage ?? 0))
      .concat(
        // ou leur libellé pour les autres
        ...autres.toSorted((a, b) => a.metier.libelle.localeCompare(b.metier.libelle))
      )
  );
}

/**
 * Permet de trier une liste de postes selon les règles suivantes:
 * - Les postes avec agent sont prioritaires sur les postes sans agent
 * - Les postes avec agent sont triés par nom d'agent
 * - Les postes sans agent sont triés par libellé de poste
 */
function sortPostes(a: Poste | PosteSansAgent, b: Poste | PosteSansAgent): number {
  if (isPosteAgent(a) && isPosteAgent(b)) {
    return a.agent.nom.localeCompare(b.agent.nom);
  } else if (isPosteAgent(a)) {
    return -1;
  } else if (isPosteAgent(b)) {
    return 1;
  }
  return a.libelle.localeCompare(b.libelle);
}

/**
 * Retourne la clé de regroupement des contacts métiers selon qu'ils soient ou non de type périmètre :
 * permet de regrouper par compétence pour les contacts métiers périmètre
 * et uniquement par métier pour les autres.
 * @param contactMetier Contact métier dont on veut la clé de regroupement
 * @returns La clé de regroupement des contacts métiers
 */
function getContactsMetiersKey(contactMetier: ContactMetier): string {
  return isContactMetierPerimetre(contactMetier)
    ? `${contactMetier.metier.code}-${contactMetier.perimetre.competence}`
    : contactMetier.metier.code;
}

/**
 * Regroupe les contacts métiers par métier (et compétence si contact métier périmètre)
 * @param contactsMetiers Liste des contacts métiers à regrouper
 * @param inclutHorsCompetenceSdea Indique si les contacts métiers avec le poste "Hors compétence du SDEA" doivent être inclus ou non
 * @returns Une liste de contacts métiers regroupés par métier (et compétence si contact métier périmètre)
 */
function getContactsMetiersGroupedByMetier(
  contactsMetiers: Array<ContactMetier>,
  inclutHorsCompetenceSdea: boolean | undefined = true
): Array<ContactMetier> {
  let contacts = [...contactsMetiers];
  // Filtrage pour n'avoir que des contacts métiers avec un poste renseigné
  if (!inclutHorsCompetenceSdea) {
    contacts = contacts
      .map((contactMetier) => {
        if (contactMetier.postes != null) {
          contactMetier.postes = contactMetier.postes.filter(
            (p) => p.libelle !== null && p.libelle !== posteSansAgent.libelle
          );
        }
        return contactMetier;
      })
      .filter((contactMetier) => contactMetier.postes != null && contactMetier.postes.length > 0);
  }

  // Regroupement des contacts métiers par métier (et compétence si contact métier périmètre)
  // avec dédoublonnage des postes
  contacts = Array.from(
    contacts
      .reduce<Map<string, ContactMetier>>((acc, value) => {
        // Entrée dans l'actuel map de contact métier
        const entry: ContactMetier = acc.get(getContactsMetiersKey(value)) ?? { ...value };

        // Map de dédoublonnage de postes
        const posteMap = new Map<string, Poste | PosteSansAgent>();
        entry.postes?.forEach((p) => posteMap.set(p.codeAstre ?? p.libelle, p));
        value.postes?.forEach((p) => posteMap.set(p.codeAstre ?? p.libelle, p));

        // Tri des postes par libellé et assignation à l'entrée actuelle
        const poste = posteMap.values().toArray().toSorted(sortPostes);
        entry.postes = poste;

        acc.set(getContactsMetiersKey(value), entry);
        return acc;
      }, new Map<string, ContactMetier>())
      .values()
  );

  return contacts;
}

export const metiersUtils = {
  getContactMetiersToDisplay,
  getContactsMetiersGroupedByMetier,
  isPosteAgent,
};
